// @ts-strict-ignore
import { faTruckFast } from "@fortawesome/pro-regular-svg-icons";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Arrange, Box, Column, Columns, Link, QuantitySelector, Typography } from "@aviary";
import { PractitionerRestockReminder } from "@clinic/shared/components/CatalogProductCard/CatalogProductDropdown/PractitionerRestockReminder/PractitionerRestockReminder";
import { useGlobalConfig } from "@clinic/shared/hooks/useGlobalConfig/useGlobalConfig";
import { AddToCartButton } from "@emerson/components/AddToCartButton/AddToCartButton";
import { emersonRoutes } from "@emerson/data/emersonRoutes";
import type { CatalogProductWithVariant } from "@emerson/data/queries/CatalogProductQuery/CatalogProduct.query";
import { l } from "@emerson/locales/i18n";
import { FlipperPriceBlur } from "@shared/components/FlipperPriceBlur/FlipperPriceBlur";
import { Price } from "@shared/components/Price/Price";
import { StateRestrictionWarning } from "@shared/components/StateRestrictionWarning/StateRestrictionWarning";
import { useWholesaleCartQuery } from "@shared/data/queries/WholesaleCartQuery/WholesaleCart.query";
import {
  EmersonCheckoutStartEntryPoint,
  EmersonCheckoutAddToCartEntryPoint,
} from "@shared/hooks/useAnalyticsLogger/EmersonAnalyticsEntryPoint";
import { BrandAgreementEntryPoint } from "@shared/hooks/useAnalyticsLogger/StoreAnalyticsEntryPoint";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";
import { FontAwesomeIcon } from "@shared/react-fontawesome/react-fontawesome";
import { AvailabilityStatus } from "@shared/types/availabilityStatus";
import { Size } from "@shared/types/sizes";
import { getCashValue } from "@shared/utils/getCashValue/getCashValue";

import { EmersonVariantSelect } from "../../EmersonVariantSelect/EmersonVariantSelect";
import { ProductUnitPricing } from "../../ProductUnitPricing/ProductUnitPricing";
import { ProductAvailabilityMessage } from "../ProductAvailabilityMessage/ProductAvailabilityMessage";

import * as styles from "./ProductActions.styles";

interface Props {
  product: CatalogProductWithVariant;
  freeShippingAmountLeft: number;
  handleSimilarProductsClick: () => void;
  setCurrentVariantId: (variantId: string) => void;
  currentVariantId: string;
  isQuickView?: boolean;
}

const ProductActions = ({
  product,
  freeShippingAmountLeft,
  handleSimilarProductsClick,
  setCurrentVariantId,
  currentVariantId,
  isQuickView = false,
}: Props) => {
  const { t } = useTranslation();
  const { desktop } = useBreakpoints();
  const { isUnauthenticated } = useSharedGlobalConfig();
  const { freeShippingThreshold } = useGlobalConfig();
  const { data } = useWholesaleCartQuery({
    skip: isUnauthenticated,
  });

  // const [wholesalePromotionEnabled] = useWholesalePromotionEnabled();

  const [quantity, setQuantity] = useState(1);

  const variant = product?.availableOrMasterVariant;

  const updateQuantity = newQuantity => {
    setQuantity(newQuantity);
  };

  const availableVariantsStatus = [
    AvailabilityStatus.UNAVAILABLE.toLowerCase(),
    AvailabilityStatus.DISCONTINUED.toLowerCase(),
    AvailabilityStatus.NA.toLowerCase(),
  ];

  // const [getWholesalePromotionDetails, { data: promotionDetailsData }] =
  //   useWholesalePromotionDetailsLazyQuery();

  const availableVariants: any = product.variants ?? [];

  const currentVariant = availableVariants.find(({ id }) => id === currentVariantId) ?? variant;
  const { availabilityStatus, expectedReturnDate, wholesalePrice, emersonRetailPrice, sku } =
    currentVariant;
  const isAddedToCart = data?.currentOffice?.wholesaleCart?.lineItems.find(
    item => item.variant.id === currentVariant?.id
  );

  const isCurrentVariantUnavailable = availableVariantsStatus.includes(
    currentVariant?.availabilityStatus?.toLowerCase()
  );
  const isDiscontinued = availabilityStatus?.toLowerCase() === "discontinued";
  const isOutOfStock =
    availabilityStatus?.toLowerCase() === "unavailable" ||
    availabilityStatus?.toLowerCase() === "n/a";

  const isNotAvailable = isDiscontinued || isOutOfStock;

  const canEnableRestockReminder = isOutOfStock;

  // const promotionDetails = wholesalePromotionEnabled
  //   ? product.availableOrMasterVariant?.wholesalePromotionDetails
  //   : null;

  // const promotion =
  //   promotionDetailsData?.viewer?.variant?.wholesalePromotionDetails || promotionDetails;

  const promotion = null;

  const handleVariantSelect = (variantId: string) => {
    setCurrentVariantId(variantId);
    // if (promotion) {
    //   getWholesalePromotionDetails({ variables: { id: variantId } });
    // }
  };

  const openChat = () => {
    if (typeof window !== "undefined") {
      window.adaEmbed.toggle();
    }
  };

  const renderFreeShippingMessage = () => {
    if (isUnauthenticated)
      return (
        <Trans i18nKey={l.productDetails.FreeShipping}>
          FREE Shipping on orders over
          <strong>{{ amount: getCashValue(freeShippingThreshold) }}!</strong>
        </Trans>
      );

    if (freeShippingAmountLeft > 0) {
      return (
        <Trans i18nKey={l.productDetails.RemainingUntilFreeShipping}>
          You're <strong>{{ amount: getCashValue(freeShippingAmountLeft) }}</strong> away from FREE
          shipping on this order!
        </Trans>
      );
    }

    if (freeShippingAmountLeft <= 0) {
      return t(l.productDetails.CongratsYouHaveFreeShipping);
    }

    return null;
  };

  const renderRestockReminder = () =>
    canEnableRestockReminder && (
      <PractitionerRestockReminder
        isButton
        variant={currentVariant}
        restockReminderOnText={t(l.productDetails.RestockReminderOn)}
      />
    );

  const renderPricing = () =>
    !isDiscontinued && (
      <Box isBordered isColor="system" padding="xxsmall" layoutStyles={styles.pricingBox}>
        <Arrange alignment="center" justify="center">
          <Typography css={styles.pricing}>
            {t(l.productDetails.YourPrice)}
            <ProductUnitPricing
              displayColor="green"
              promotionPrice={promotion?.price}
              sellPrice={wholesalePrice}
            />
            {t(l.productDetails.MSRP)}
            <FlipperPriceBlur>
              <Price price={emersonRetailPrice} />
            </FlipperPriceBlur>
          </Typography>
        </Arrange>
      </Box>
    );

  const renderAvailabilityMessage = () =>
    !isUnauthenticated && (
      <ProductAvailabilityMessage
        availabilityStatus={availabilityStatus}
        expectedReturnDate={expectedReturnDate}
      />
    );

  const renderQuantitySelector = () =>
    !isAddedToCart && (
      <>
        <Typography isSecondaryWeight>{t(l.productDetails.SelectQuantity)}</Typography>

        <QuantitySelector
          value={quantity}
          handleSetValue={updateQuantity}
          disabled={isNotAvailable}
        />
      </>
    );

  const renderProductInfo = () => {
    if (isUnauthenticated)
      return (
        <Box isBordered padding="xsmall">
          <Arrange alignment="center" justify="center">
            <Typography isMarginless>
              <Trans i18nKey={l.productDetails.Unauthenticated}>
                <Link to={emersonRoutes.login}>Sign in</Link> to see pricing and more details
              </Trans>
            </Typography>
          </Arrange>
        </Box>
      );

    return (
      <Arrange isVertical isFullWidth>
        <Columns preserveMobileColumns isMarginless>
          <Column columnWidth={6} isPaddingless>
            <Arrange spacing="xsmall" isVertical>
              <Typography isSecondaryWeight>{t(l.productDetails.SelectSize)}</Typography>
              <EmersonVariantSelect
                loading={availableVariants.length !== product.variantCount}
                onVariantSelect={handleVariantSelect}
                selectedVariantId={currentVariant.id}
                variantCount={product.variantCount}
                variants={availableVariants}
              />
            </Arrange>
          </Column>
          <Column columnWidth={6} isPaddingless>
            <Arrange spacing="xsmall" isVertical>
              {renderQuantitySelector()}
            </Arrange>
          </Column>
        </Columns>
        <Arrange isVertical isFullWidth spacing="xsmall" css={styles.container}>
          {renderPricing()}
          <AddToCartButton
            initialQuantity={quantity}
            variantId={currentVariantId}
            productId={product.id}
            availabilityStatus={availabilityStatus}
            analyticsEntryPoint={
              isQuickView
                ? EmersonCheckoutStartEntryPoint.QUICK_VIEW
                : EmersonCheckoutStartEntryPoint.CATALOG_PRODUCTPAGE_VIEWCART
            }
            entryPoint={
              isQuickView
                ? EmersonCheckoutAddToCartEntryPoint.QUICK_VIEW
                : EmersonCheckoutAddToCartEntryPoint.PRODUCT_DETAILS_PAGE
            }
            sku={sku}
            brandName={product.brand.name}
            defaultVariant={product?.availableOrMasterVariant?.sku}
            isAnyVariantUnavailable={isCurrentVariantUnavailable}
            showQuantitySelector={false}
            containsHemp={product?.containsCbd}
            similarProductsEnabled
            overrideButtonWrapper
            isPrimary
            isFullWidth
            buttonSize={Size.LARGE}
            handleSimilarProductsClick={handleSimilarProductsClick}
            isAddedButtonTextOnly={false}
            brandAgreementEntryPoint={
              isQuickView
                ? BrandAgreementEntryPoint.QUICK_VIEW
                : BrandAgreementEntryPoint.PRODUCT_DETAILS_PAGE
            }
          />
        </Arrange>
        {renderRestockReminder()}
      </Arrange>
    );
  };

  return (
    <Arrange isVertical isFullWidth spacing="large" css={styles.container}>
      {renderAvailabilityMessage()}
      <StateRestrictionWarning
        containsCbd={product?.containsCbd}
        stateRestricted={product?.nonShippableStates}
      />
      {renderProductInfo()}
      <Box isBordered isColor="success" padding="xxsmall">
        <Arrange alignment="center" justify="center" isWrapped={false}>
          <FontAwesomeIcon icon={faTruckFast} />
          <Typography>{renderFreeShippingMessage()}</Typography>
        </Arrange>
      </Box>
      <Box isBordered padding="small">
        <Arrange justify="center" spacing="xsmall">
          <Typography type={desktop.lessThan ? "footnote" : "body"}>
            <Trans i18nKey={l.productDetails.contactQuestions}>
              Questions? Visit our&nbsp;
              <Link isColor="system" href={emersonRoutes.external.helpCenter} noExternalIcon>
                Help Center
              </Link>
              or contact our support team:&nbsp;
              <Link isColor="system" onClick={openChat}>
                Chat
              </Link>
              &nbsp;,&nbsp;
              <Link isColor="system" href="mailto: cs@emersonecologics.com">
                Email
              </Link>
              &nbsp; or Call 800-654-4432
            </Trans>
          </Typography>
          <Typography isSubdued type="footnote">
            {t(l.productDetails.contactAvailable)}
          </Typography>
          <Typography isSubdued type="footnote">
            {t(l.productDetails.contactTimeframe)}
          </Typography>
        </Arrange>
      </Box>
    </Arrange>
  );
};

export { ProductActions };
